import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Send,
  Loader2,
  Truck,
  Package,
  Search,
  Save,
  X,
  Pencil,
  CreditCard,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { CustomerSearchModal } from '@components/CustomerSearchModal';
import { DateInputBR } from '@components/DateInputBR';
import { invoiceService } from '../../services/invoiceService';
import { invoiceTransactionTypeService } from '../../services/invoiceTransactionTypeService';
import { productService } from '../../services/productService';
import { companyService } from '../../services/companyService';
import { salesOrderService } from '../../services/salesOrderService';
import { shippingProviderService } from '../../services/shippingProviderService';
import { orderTypeService } from '../../services/orderTypeService';
import { Company, formatCnpj } from '../../types/company';
import { Product } from '../../types/product';
import { InvoiceItemPayload, InvoiceEmitError, InvoiceTransportePayload, INVOICE_MODEL_OPTIONS, INVOICE_MODEL_DEFAULT } from '../../types/invoice';
import { InvoiceTransactionType } from '../../types/invoiceTransactionType';
import { useCepSearch } from '../../hooks/useCepSearch';
import { SalesOrder, OrderFreightQuote, Customer } from '../../types/salesOrder';
import { resolveInvoiceUnitValue } from './invoiceItemPricing';
import { isEcommerceOrder, giftDiscountFromOrder } from './orderGiftInvoice';
import {
  missingDestAddressFields,
  destAddressErrorMessage,
  fillMissingDestAddress,
  filledDestAddressLabels,
  destAddressTooLong,
  NFE_ADDRESS_MAX_LEN,
} from './destAddress';
import { ShippingProvider, ShippingProviderService } from '../../types/shippingProvider';
import { Vehicle } from '../../types/vehicle';
import { vehicleService } from '../../services/vehicleService';
import { OrderType } from '../../types/orderType';
import { InvoiceValidationErrorsModal, InvoiceEmitErrorModal } from '../../components/InvoiceErrorModals';
import { buildInvoicePaymentPlan, effectiveOrderFreight } from './paymentPlan';
import { resolveDestFiscal } from '../../utils/destFiscal';

// ─── Local types ──────────────────────────────────────────────────

interface FormItem extends InvoiceItemPayload {
  _tempId: number;
}

interface InvoiceNavState {
  orderData?: SalesOrder;
  invoiceMode?: 'sales' | 'gift';
  forceFullGiftOrder?: boolean;
  // Pré-preenchimento vindo de um CONVITE (Pedido de Entrega, módulo Eventos):
  // NF de bonificação MANUAL (sem pedido de venda), natureza "Outra Saída de
  // Mercadoria" (CFOP 5.949/6.949) pré-selecionada; o vínculo volta ao convite via
  // delivery_order_id no payload da emissão.
  deliveryOrderPrefill?: {
    delivery_order_id: number;
    event_id?: number;
    recipient_name?: string;
    recipient_document?: string;
    recipient_phone?: string;
    recipient_email?: string;
    ship_addr_street?: string;
    ship_addr_num?: string;
    ship_addr_compl?: string;
    ship_addr_district?: string;
    ship_addr_city?: string;
    ship_addr_state?: string;
    ship_addr_zip?: string;
    // Produto do convite (cadastro do evento): vira o item único da NF.
    invitation_product_sku?: string;
    // Empresa emissora do EVENTO: pré-selecionada e travada, como na NF de pedido.
    company_id?: number;
    // Transportadora carimbada no convite pela cotação selecionada — pré-seleciona a
    // modalidade "carrier" (Viver Organizer dispara o fluxo de escolha do veículo).
    provider_id?: number;
    provider_service_id?: number;
  };
}

const buildItemsFromOrder = (
  orderData: SalesOrder | undefined,
  isGiftInvoice: boolean,
  isOrderTypeFullGift: boolean,
): FormItem[] => {
  if (!orderData?.items || orderData.items.length === 0) return [];

  // Pedido do site com brinde de promoção sai em UMA nota de venda: o brinde é
  // declarado a valor cheio e o valor dele vira desconto do pedido (ver
  // giftDiscountFromOrder). Emitir duas notas exigiria dois pedidos de venda, e a
  // mercadoria toda vai no mesmo pacote — um pacote, uma nota.
  //
  // Escopo deliberado: SÓ pedido do site. Brinde de proposta do CRM e componente
  // de kit seguem indo na NF de bonificação, como sempre.
  const siteGiftInSalesInvoice = !isGiftInvoice && isEcommerceOrder(orderData);

  const filtered = orderData.items.filter(i => {
    if (isGiftInvoice) {
      // Para pedido inteiro-presente, todos os itens vão para NF de bonificação.
      if (isOrderTypeFullGift) return true;
      // Brinde de promoção do site já está na nota de venda; não pode sair numa
      // segunda nota também.
      if (isEcommerceOrder(orderData)) return false;
      return i.is_gift === true;
    }
    if (siteGiftInSalesInvoice) return true;
    return i.is_gift !== true;
  });

  return filtered.map(i => ({
    _tempId: nextTempId(),
    code: i.sku,
    description: i.description || '',
    unit: 'UN',
    quantity: i.quantity,
    // Valor cheio (sem o desconto de 100% do item) na bonificação E no brinde de
    // promoção do site dentro da nota de venda. Ver resolveInvoiceUnitValue.
    unit_value: resolveInvoiceUnitValue(
      i.unit_price || 0,
      i.discount || 0,
      isGiftInvoice || (siteGiftInSalesInvoice && i.is_gift === true),
    ),
    ncm: '',
    cfop: '',
    type: 'P',
    origin: 0,
  }));
};

// ─── Helpers ──────────────────────────────────────────────────────

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

let _tempSeq = 1;
const nextTempId = () => _tempSeq++;

// Error modals imported from ../../components/InvoiceErrorModals


// ─── Component Props ──────────────────────────────────────────────

interface InvoiceFormViewProps {
  /**
   * Scope for filtering transaction types: 'sales' (default), 'supply',
   * 'gift' (Remessa em bonificação, doação ou brinde), or undefined
   * (no filter).
   *
   * Pode ser sobrescrito pelo `location.state.invoiceMode` quando vindo
   * de um pedido de venda ("sales" → NF de venda; "gift" → NF de brinde).
   */
  transactionTypeScope?: 'sales' | 'supply' | 'gift';
  /** Path to navigate back to on cancel / after creation */
  listPath?: string;
}

// ─── Component ────────────────────────────────────────────────────

export const InvoiceFormView: React.FC<InvoiceFormViewProps> = ({
  transactionTypeScope = 'sales',
  listPath = '/invoices',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchCep } = useCepSearch();

  const navState = (location.state as InvoiceNavState | null) ?? {};
  // Dados do pedido passados via navigation state
  const orderData = navState.orderData;
  const isFromOrder = Boolean(orderData);

  // Convite (Pedido de Entrega): NF de bonificação manual pré-preenchida.
  const deliveryOrderPrefill = navState.deliveryOrderPrefill;
  const isFromDeliveryOrder = Boolean(deliveryOrderPrefill);

  // Modo da nota quando vinda de um pedido de venda: 'sales' (padrão)
  // ou 'gift' (Remessa em bonificação, doação ou brinde). O convite entra
  // sempre como 'gift' (nota sem cobrança).
  const invoiceMode = isFromDeliveryOrder ? 'gift' : (navState.invoiceMode ?? 'sales');
  const isGiftInvoice = invoiceMode === 'gift';
  // Quando vier de um pedido de venda (ou de um convite), o modo (sales/gift)
  // determina o escopo das naturezas. Caso contrário, usa o escopo da prop.
  const effectiveScope: 'sales' | 'supply' | 'gift' | undefined =
    isFromOrder || isFromDeliveryOrder ? invoiceMode : transactionTypeScope;

  // ── General state ─────────────────────────────────────────────
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<InvoiceTransactionType[]>([]);
  const [createError, setCreateError] = useState<InvoiceEmitError | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string> | null>(null);

  // ── Form header fields ────────────────────────────────────────
  const [companyId, setCompanyId] = useState<number | ''>(orderData?.company_id || deliveryOrderPrefill?.company_id || '');
  const [transactionTypeId, setTransactionTypeId] = useState<number | ''>('');
  const [tipoNF, setTipoNF] = useState(1); // 1=Saída (padrão)
  const [finalidade, setFinalidade] = useState(1); // 1=Normal
  const [model, setModel] = useState<number>(INVOICE_MODEL_DEFAULT); // 55=NF-e, 65=NFC-e
  const [isSimpleTax, setIsSimpleTax] = useState(true);
  const [observacoes, setObservacoes] = useState('');

  // ── Customer ──────────────────────────────────────────────────
  const [customerId, setCustomerId] = useState<number | undefined>(orderData?.customer_id);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerName, setCustomerName] = useState(orderData?.customer_name || '');
  const [customerDocument, setCustomerDocument] = useState(orderData?.customer_document || '');
  const [customerTipoPessoa, setCustomerTipoPessoa] = useState<string>(() => {
    const doc = orderData?.customer_document?.replace(/\D/g, '') || '';
    return doc.length === 14 ? 'J' : 'F';
  });
  // Contribuinte ICMS (indIEDest) e IE vêm do CADASTRO do cliente, não de um default fixo:
  // mandar "9 - não contribuinte" para um CNPJ com IE ativa faz a SEFAZ rejeitar (cStat 232).
  // A regra é ciente da UF de destino (isento não vale em várias UFs nem no DF — CF/DF).
  // Na emissão o backend ainda consulta o cadastro da SEFAZ; se divergir, retorna
  // DEST_FISCAL_DIVERGENCE com os valores corretos e a tela se auto-corrige (sem override
  // silencioso).
  const orderDestFiscal = resolveDestFiscal(
    {
      document: orderData?.customer_document,
      stateRegistrationTaxId: orderData?.customer_state_registration_tax_id,
      nonContributorIE: orderData?.customer_non_contributor_ie,
    },
    orderData?.ship_addr_state
  );
  const [customerContribuinte, setCustomerContribuinte] = useState(orderDestFiscal.contribuinte);
  const [customerIE, setCustomerIE] = useState(orderDestFiscal.ie);
  const [customerPhone, setCustomerPhone] = useState(orderData?.customer_cell_phone || '');
  const [customerEmail, setCustomerEmail] = useState(orderData?.customer_email || '');

  // ── Endereço do destinatário da NF-e (dest/enderDest) ─────────
  // Rótulos dos campos que o ViaCEP completou automaticamente, para avisar o operador —
  // preenchimento silencioso de campo fiscal não é aceitável.
  const [autoCepFilled, setAutoCepFilled] = useState<string[]>([]);
  const [addrStreet, setAddrStreet] = useState(orderData?.ship_addr_street || '');
  const [addrNum, setAddrNum] = useState(orderData?.ship_addr_num || '');
  const [addrCompl, setAddrCompl] = useState(orderData?.ship_addr_compl || '');
  const [addrDistrict, setAddrDistrict] = useState(orderData?.ship_addr_district || '');
  const [addrCity, setAddrCity] = useState(orderData?.ship_addr_city || '');
  const [addrState, setAddrState] = useState(orderData?.ship_addr_state || '');
  const [addrZip, setAddrZip] = useState(orderData?.ship_addr_zip || '');

  // Convite (Pedido de Entrega): aplica o snapshot do convidado UMA vez — a NF é
  // manual (sem pedido de venda), com destinatário/endereço vindos do convite e o
  // item (produto do convite) adicionado pelo efeito de itens abaixo.
  useEffect(() => {
    if (!deliveryOrderPrefill) return;
    setCustomerName(deliveryOrderPrefill.recipient_name || '');
    setCustomerDocument(deliveryOrderPrefill.recipient_document || '');
    const doc = (deliveryOrderPrefill.recipient_document || '').replace(/\D/g, '');
    setCustomerTipoPessoa(doc.length === 14 ? 'J' : 'F');
    setCustomerPhone(deliveryOrderPrefill.recipient_phone || '');
    setCustomerEmail(deliveryOrderPrefill.recipient_email || '');
    setAddrStreet(deliveryOrderPrefill.ship_addr_street || '');
    setAddrNum(deliveryOrderPrefill.ship_addr_num || '');
    setAddrCompl(deliveryOrderPrefill.ship_addr_compl || '');
    setAddrDistrict(deliveryOrderPrefill.ship_addr_district || '');
    setAddrCity(deliveryOrderPrefill.ship_addr_city || '');
    setAddrState(deliveryOrderPrefill.ship_addr_state || '');
    setAddrZip(deliveryOrderPrefill.ship_addr_zip || '');
    // Transportadora do convite (cotação selecionada): pré-seleciona a entrega por
    // transportadora — para a Viver Organizer isso carrega frota/veículos (placa).
    if (deliveryOrderPrefill.provider_id) {
      setDeliveryMethod('carrier');
      setProviderId(deliveryOrderPrefill.provider_id);
      if (deliveryOrderPrefill.provider_service_id) {
        setProviderServiceId(deliveryOrderPrefill.provider_service_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [shippingServiceName, setShippingServiceName] = useState(orderData?.shipping_chosen_by_customer || '');
  const [volumeQuantity, setVolumeQuantity] = useState<number | ''>(() => {
    if (orderData?.volumes && orderData.volumes.length > 0) return orderData.volumes.length;
    return '';
  });
  const [totalWeight, setTotalWeight] = useState<number | ''>(() => {
    if (orderData?.volumes && orderData.volumes.length > 0) {
      return orderData.volumes.reduce((sum, v) => sum + (v.weight || 0), 0);
    }
    return '';
  });

  // ── Delivery method & transport ──────────────────────────────
  // 'carrier' = enviado por transportadora; 'pickup' = retirado pelo cliente
  const [deliveryMethod, setDeliveryMethod] = useState<'carrier' | 'pickup' | ''>('');
  const [fretePorConta, setFretePorConta] = useState<number>(0); // 0=CIF default
  const [freightValue, setFreightValue] = useState<number>(0);
  // NF de venda manual (sem pedido): desconto (subtrai) e frete (soma) informados na tela.
  const [manualDiscount, setManualDiscount] = useState<number>(0);
  const [manualFreight, setManualFreight] = useState<number>(0);
  const [providerId, setProviderId] = useState<number | ''>('');
  const [providerServiceId, setProviderServiceId] = useState<number | ''>('');
  const [shippingProviders, setShippingProviders] = useState<ShippingProvider[]>([]);
  const [shippingServices, setShippingServices] = useState<ShippingProviderService[]>([]);
  const [freightQuotes, setFreightQuotes] = useState<OrderFreightQuote[]>([]);
  const [loadingFreight, setLoadingFreight] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [orderTypeLookup, setOrderTypeLookup] = useState<OrderType | null>(null);

  // ── Transportador (dados fiscais do provider) ─────────────────
  const [transportadorNome, setTransportadorNome] = useState('');
  const [transportadorDocumento, setTransportadorDocumento] = useState('');
  const [transportadorIE, setTransportadorIE] = useState('');
  const [transportadorEndereco, setTransportadorEndereco] = useState('');
  const [transportadorMunicipio, setTransportadorMunicipio] = useState('');
  const [transportadorUF, setTransportadorUF] = useState('');

  // ── Veículo ───────────────────────────────────────────────────
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | ''>('');
  const [veiculoPlaca, setVeiculoPlaca] = useState('');
  const [veiculoUF, setVeiculoUF] = useState('');
  const [veiculoMarca, setVeiculoMarca] = useState('');
  const [selectedProviderHasVehicles, setSelectedProviderHasVehicles] = useState(false);

  const isOrderTypeFullGift = Boolean(
    navState.forceFullGiftOrder
    || (orderTypeLookup?.generate_invoice && !orderTypeLookup.has_financial_value),
  );
  const invalidSalesModeForFullGift = isFromOrder && isOrderTypeFullGift && !isGiftInvoice;

  const selectedTransactionType = transactionTypes.find(
    (t) => t.invoice_transaction_type_id === transactionTypeId,
  );
  const shouldForceZeroFreight = isGiftInvoice || selectedTransactionType?.is_billable === false;
  const shouldIncludeInstallments = !isGiftInvoice && selectedTransactionType?.is_billable === true;

  // ── Items (inline) ────────────────────────────────────────────
  const [items, setItems] = useState<FormItem[]>(() =>
    buildItemsFromOrder(orderData, isGiftInvoice, Boolean(navState.forceFullGiftOrder)),
  );
  const [itemShowNew, setItemShowNew] = useState(false);

  // Convite: adiciona o produto do convite (cadastro do evento) como item único da NF,
  // com descrição/preço do catálogo — o NCM é resolvido pelo backend via SKU.
  useEffect(() => {
    const sku = deliveryOrderPrefill?.invitation_product_sku;
    if (!sku) return;
    let cancelado = false;
    productService.searchProducts(sku, 1, 5)
      .then(({ products }) => {
        if (cancelado) return;
        const product = products.find(p => p.sku === sku) || products[0];
        if (!product) return;
        setItems(prev => (prev.length > 0 ? prev : [{
          _tempId: nextTempId(),
          code: product.sku,
          description: product.description || '',
          unit: 'UN',
          quantity: 1,
          unit_value: product.price || 0,
          ncm: '',
          cfop: '',
          type: 'P',
          origin: 0,
        }]));
      })
      .catch(() => { /* item pode ser adicionado manualmente */ });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [newItemForm, setNewItemForm] = useState<Partial<InvoiceItemPayload>>({
    code: '', description: '', unit: 'UN', quantity: 1, unit_value: 0, ncm: '', type: 'P', origin: 0,
  });
  const [itemProductQuery, setItemProductQuery] = useState('');
  const [itemProductResults, setItemProductResults] = useState<Product[]>([]);
  const [itemProductLoading, setItemProductLoading] = useState(false);
  const [itemProductOpen, setItemProductOpen] = useState(false);
  const itemProductDebounce = useRef<ReturnType<typeof setTimeout>>();

  // ── Installments (from order) ─────────────────────────────────
  interface InvoiceInstallment {
    _id: number;
    value: number;
    due_date: string;
    payment_method_id?: number;
    payment_method_name?: string;
    notes?: string;
  }
  let _instSeq = 1;
  const nextInstId = () => _instSeq++;
  const [invoiceInstallments, setInvoiceInstallments] = useState<InvoiceInstallment[]>(() => {
    if (!orderData?.installments) return [];
    return orderData.installments.map(inst => ({
      _id: nextInstId(),
      value: inst.value,
      due_date: inst.due_date,
      payment_method_id: inst.payment_method_id,
      payment_method_name: inst.payment_method_name,
      notes: inst.notes,
    }));
  });
  const [installmentEditId, setInstallmentEditId] = useState<number | null>(null);
  const [installmentForm, setInstallmentForm] = useState<Partial<InvoiceInstallment>>({});
  const [paymentMethodsLookup, setPaymentMethodsLookup] = useState<{ payment_method_id: number; payment_method_name: string; is_product_credit_4_return?: boolean }[]>([]);

  // Load payment methods for installment editing
  useEffect(() => {
    salesOrderService.getPaymentMethods().then(setPaymentMethodsLookup).catch(() => {});
  }, []);

  // ── Load lookups ──────────────────────────────────────────────
  useEffect(() => {
    companyService.getCompanies()
      .then(list => {
        setCompanies(list);
        if (!orderData && !deliveryOrderPrefill?.company_id && list.length === 1) setCompanyId(list[0].company_id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isFromOrder || !orderData?.order_type_id || navState.forceFullGiftOrder) return;
    orderTypeService.getById(orderData.order_type_id)
      .then(setOrderTypeLookup)
      .catch(() => setOrderTypeLookup(null));
  }, [isFromOrder, orderData?.order_type_id, navState.forceFullGiftOrder]);

  useEffect(() => {
    if (!isFromOrder) return;
    setItems(buildItemsFromOrder(orderData, isGiftInvoice, isOrderTypeFullGift));
  }, [isFromOrder, orderData, isGiftInvoice, isOrderTypeFullGift]);

  // Carregar naturezas de operação ATIVAS ao mudar de empresa
  useEffect(() => {
    if (companyId) {
      invoiceTransactionTypeService.getActiveByCompany(
        companyId as number,
        effectiveScope,
      )
        .then(types => {
          setTransactionTypes(types);
          // Convite (Pedido de Entrega): pré-seleciona a natureza pela operação fiscal
          // declarada no cadastro (CFOPOperation = OTHER_OUTPUT ⇒ CFOP 5.949/6.949),
          // em vez do palpite por substring do nome.
          if (isFromDeliveryOrder && !transactionTypeId) {
            const outraSaida = types.find(t => t.cfop_operation === 'OTHER_OUTPUT');
            if (outraSaida) setTransactionTypeId(outraSaida.invoice_transaction_type_id);
          }
          // Auto-selection quando vindo de um pedido de venda
          if (isFromOrder && !transactionTypeId) {
            if (isGiftInvoice) {
              // Procura por "bonificação", "brinde" ou "doação" no nome
              const giftType = types.find(t => {
                const n = t.invoice_transaction_type_name.toLowerCase();
                return n.includes('bonifica') || n.includes('brinde') || n.includes('doaç') || n.includes('doac');
              });
              if (giftType) setTransactionTypeId(giftType.invoice_transaction_type_id);
            } else {
              const vendaType = types.find(t =>
                t.invoice_transaction_type_name.toLowerCase().includes('venda de mercadoria')
              );
              if (vendaType) setTransactionTypeId(vendaType.invoice_transaction_type_id);
            }
          }
        })
        .catch(() => setTransactionTypes([]));
    } else {
      setTransactionTypes([]);
      setTransactionTypeId('');
    }
  }, [companyId, effectiveScope]);

  // ── CEP auto-fill ──────────────────────────────────────────────
  // Digitar o CEP é ação explícita do operador: sobrescreve os 4 campos que o ViaCEP conhece.
  const handleCepChange = async (value: string) => {
    setAddrZip(value);
    setAutoCepFilled([]);
    const clean = value.replace(/\D/g, '');
    if (clean.length === 8) {
      const result = await searchCep(clean);
      if (result) {
        setAddrStreet(result.logradouro || '');
        setAddrDistrict(result.bairro || '');
        setAddrCity(result.localidade || '');
        setAddrState(result.uf || '');
      }
    }
  };

  // ── Completar pelo CEP o que veio faltando ────────────────────
  // Alguns pedidos chegam com o endereço do destinatário incompleto — o ponto de retirada do
  // e-commerce, por exemplo, vem sem bairro. Havendo CEP e algum campo obrigatório vazio,
  // consulta o ViaCEP e preenche SÓ o que falta: o que veio do pedido ou do cadastro do cliente
  // nunca é sobrescrito (é dado fiscal, e a base de CEP não conhece número nem complemento).
  // O ref garante uma consulta por CEP, e não voltar a preencher um campo que o operador apagou.
  const autoCepTried = useRef<string | null>(null);
  useEffect(() => {
    const clean = addrZip.replace(/\D/g, '');
    if (clean.length !== 8 || autoCepTried.current === clean) return;

    const atual = { street: addrStreet, district: addrDistrict, city: addrCity, state: addrState, zip: addrZip };
    if (missingDestAddressFields(atual).length === 0) return;
    autoCepTried.current = clean;

    let cancelado = false;
    searchCep(clean).then(result => {
      if (cancelado || !result) return;
      const patch = fillMissingDestAddress(atual, result);
      const preenchidos = filledDestAddressLabels(patch);
      if (preenchidos.length === 0) return;
      if (patch.street !== undefined) setAddrStreet(patch.street);
      if (patch.district !== undefined) setAddrDistrict(patch.district);
      if (patch.city !== undefined) setAddrCity(patch.city);
      if (patch.state !== undefined) setAddrState(patch.state);
      setAutoCepFilled(preenchidos);
    });
    return () => { cancelado = true; };
  }, [addrZip, addrStreet, addrDistrict, addrCity, addrState, searchCep]);

  // ── Load freight quotes from order ────────────────────────────
  useEffect(() => {
    if (!isFromOrder || !orderData?.order_id) return;
    setLoadingFreight(true);
    salesOrderService.getFreightQuotes(orderData.order_id)
      .then(quotes => {
        setFreightQuotes(quotes);
        const selected = quotes.find(q => q.is_selected);
        if (selected) {
          // Pre-fill with selected freight quote
          setDeliveryMethod('carrier');
          setFretePorConta(0); // CIF
          // Frete cobrado do cliente = ShippingPrice − FreightDiscountValue (mesma base do
          // OrderSellTotal). O backend deriva isso de qualquer forma; aqui só exibimos coerente.
          setFreightValue(shouldForceZeroFreight ? 0 : effectiveOrderFreight(orderData.shipping_price || 0, orderData.freight_discount_value || 0));
          setProviderId(selected.provider_id);
          setProviderServiceId(selected.provider_service_id);
          setShippingServiceName(selected.provider_service_name || selected.provider_name || '');
        }
      })
      .catch(() => setFreightQuotes([]))
      .finally(() => setLoadingFreight(false));
  }, [isFromOrder, orderData?.order_id, orderData?.shipping_price, orderData?.freight_discount_value, shouldForceZeroFreight]);

  useEffect(() => {
    if (shouldForceZeroFreight) {
      setFreightValue(0);
    }
  }, [shouldForceZeroFreight]);

  // ── Load shipping providers & services when delivery method = carrier ─
  useEffect(() => {
    if (deliveryMethod !== 'carrier') return;
    // Only load if no freight quote was auto-selected
    const hasSelectedQuote = freightQuotes.some(q => q.is_selected);
    if (hasSelectedQuote && providerId) return;

    setLoadingProviders(true);
    Promise.all([
      shippingProviderService.getProviders(),
      shippingProviderService.getProviderServices(),
    ])
      .then(([providers, services]) => {
        setShippingProviders(providers);
        setShippingServices(services);
      })
      .catch(() => {})
      .finally(() => setLoadingProviders(false));
  }, [deliveryMethod]);

  // ── Load provider fiscal data (transportador) when provider changes ──
  useEffect(() => {
    if (!providerId || deliveryMethod !== 'carrier') {
      setTransportadorNome('');
      setTransportadorDocumento('');
      setTransportadorIE('');
      setTransportadorEndereco('');
      setTransportadorMunicipio('');
      setTransportadorUF('');
      setSelectedProviderHasVehicles(false);
      setVehicles([]);
      setSelectedVehicleId('');
      setVeiculoPlaca('');
      setVeiculoUF('');
      setVeiculoMarca('');
      return;
    }
    shippingProviderService.getProviderById(providerId as number)
      .then(provider => {
        // Transportadora própria (is_own_carrier): o transportador da NF-e segue a empresa
        // emitente selecionada — o backend faz a mesma substituição na emissão, aqui é só
        // para a tela refletir o que sai no XML. Trocar a empresa re-executa este efeito.
        const company = provider.is_own_carrier
          ? companies.find(c => c.company_id === companyId)
          : undefined;
        if (company) {
          setTransportadorNome(company.name || provider.provider_name || '');
          setTransportadorDocumento(company.document || '');
          setTransportadorIE(company.state_registration_tax_id || '');
          setTransportadorEndereco(
            [company.addr_street, company.addr_num, company.addr_compl].filter(Boolean).join(', '),
          );
          setTransportadorMunicipio(company.addr_city || '');
          setTransportadorUF(company.addr_state || '');
        } else {
          setTransportadorNome(provider.provider_name || '');
          setTransportadorDocumento(provider.document || '');
          setTransportadorIE(provider.state_registration_tax_id || '');
          setTransportadorEndereco(provider.fiscal_address || '');
          setTransportadorMunicipio(provider.fiscal_city || '');
          setTransportadorUF(provider.fiscal_state || '');
        }
        setSelectedProviderHasVehicles(provider.has_vehicles ?? false);
      })
      .catch(() => {});
  }, [providerId, deliveryMethod, companyId, companies]);

  // ── Load vehicles when provider has_vehicles = true ──────────
  useEffect(() => {
    if (!selectedProviderHasVehicles) {
      setVehicles([]);
      return;
    }
    setLoadingVehicles(true);
    vehicleService.getActiveVehicles()
      .then(setVehicles)
      .catch(() => setVehicles([]))
      .finally(() => setLoadingVehicles(false));
  }, [selectedProviderHasVehicles]);

  // ── Auto-fill vehicle data on selection ──────────────────────
  useEffect(() => {
    if (!selectedVehicleId) return;
    const vehicle = vehicles.find(v => v.vehicle_id === selectedVehicleId);
    if (vehicle) {
      setVeiculoPlaca(vehicle.license_plate || '');
      setVeiculoUF(vehicle.fiscal_state || '');
      setVeiculoMarca(vehicle.manufacturer || '');
    }
  }, [selectedVehicleId, vehicles]);

  // ── Handle delivery method change ─────────────────────────────
  // ATENÇÃO: os campos addr* são o endereço do DESTINATÁRIO da NF-e (grupo dest/enderDest),
  // obrigatório em QUALQUER modalidade de frete — não são "endereço de transporte". Trocar a
  // modalidade só mexe no que é transporte (frete, provedor, transportador, veículo); o
  // endereço NUNCA é limpo nem restaurado aqui. Limpá-lo na retirada era o que fazia a
  // emissão devolver HTTP 400 ("Campos Obrigatórios": Logradouro, Bairro, Cidade, UF, CEP),
  // e restaurá-lo do pedido ao voltar para transportadora sobrescreveria o endereço que o
  // operador acabou de completar pelo CEP. O endereço vem da carga do pedido, da busca de
  // cliente ou da edição manual — e só de lá.
  const handleDeliveryMethodChange = (method: 'carrier' | 'pickup') => {
    setDeliveryMethod(method);
    if (method === 'pickup') {
      // Retirada pelo cliente: sem transporte
      setFretePorConta(9); // 9=Sem transporte
      setFreightValue(0);
      setProviderId('');
      setProviderServiceId('');
      setShippingServiceName('');
      // Clear transport data
      setTransportadorNome('');
      setTransportadorDocumento('');
      setTransportadorIE('');
      setTransportadorEndereco('');
      setTransportadorMunicipio('');
      setTransportadorUF('');
      setSelectedVehicleId('');
      setVeiculoPlaca('');
      setVeiculoUF('');
      setVeiculoMarca('');
      setSelectedProviderHasVehicles(false);
    } else {
      // Enviado por transportadora: CIF
      setFretePorConta(0); // 0=CIF
      // Try to re-apply selected freight quote
      const selected = freightQuotes.find(q => q.is_selected);
      if (selected) {
        setFreightValue(shouldForceZeroFreight ? 0 : effectiveOrderFreight(orderData?.shipping_price || 0, orderData?.freight_discount_value || 0));
        setProviderId(selected.provider_id);
        setProviderServiceId(selected.provider_service_id);
        setShippingServiceName(selected.provider_service_name || selected.provider_name || '');
      }
    }
  };

  // ── Product search for items ──────────────────────────────────

  const handleItemProductSearch = (query: string) => {
    setItemProductQuery(query);
    setNewItemForm(f => ({ ...f, code: '', description: '' }));
    clearTimeout(itemProductDebounce.current);
    if (!query.trim()) {
      setItemProductResults([]);
      setItemProductOpen(false);
      return;
    }
    setItemProductOpen(true);
    setItemProductLoading(true);
    itemProductDebounce.current = setTimeout(async () => {
      try {
        const result = await productService.searchProducts(query.trim(), 1, 10, { active: true });
        setItemProductResults(result.products || []);
      } catch {
        setItemProductResults([]);
      } finally {
        setItemProductLoading(false);
      }
    }, 300);
  };

  const handleItemProductSelect = (product: Product) => {
    setNewItemForm(f => ({
      ...f,
      code: product.sku,
      description: product.description,
      unit_value: product.price ?? f.unit_value,
      ncm: product.ncm || f.ncm,
      origin: product.product_origin_id ?? f.origin,
    }));
    setItemProductQuery(product.description);
    setItemProductOpen(false);
    setItemProductResults([]);
  };

  const addItem = () => {
    if (!newItemForm.code) {
      setError('Selecione um produto da lista antes de adicionar.');
      return;
    }
    setItems(prev => [...prev, { ...(newItemForm as InvoiceItemPayload), _tempId: nextTempId() }]);
    setNewItemForm({ code: '', description: '', unit: 'UN', quantity: 1, unit_value: 0, ncm: '', type: 'P', origin: 0 });
    setItemProductQuery('');
    setItemShowNew(false);
    setError(null);
  };

  const removeItem = (tempId: number) => {
    setItems(prev => prev.filter(i => i._tempId !== tempId));
  };

  // ── Computed totals ───────────────────────────────────────────

  const itemsTotal = items.reduce((s, i) => s + (i.unit_value || 0) * i.quantity, 0);

  // Total da nota = produtos - desconto + frete + outras despesas. No pedido, desconto/frete/
  // outras despesas vêm do pedido e da modalidade de entrega; na NF manual (sem pedido) são
  // digitados na tela. As outras despesas (vOutro, ex.: despesas financeiras do parcelamento)
  // compõem o OrderSellTotal — logo as parcelas — então entram no total, coerente com o backend
  // (que emite vOutro derivado de Order.OtherExpenses).
  // Brinde de promoção do site entra na nota a valor cheio (buildItemsFromOrder) e
  // o valor dele é abatido aqui — é o que mantém o vNF igual ao que a cliente
  // pagou. Zero em qualquer outro pedido.
  const giftDiscount = giftDiscountFromOrder(orderData);
  const effectiveDiscount = isFromOrder ? (orderData?.order_discount ?? 0) + giftDiscount : manualDiscount;
  const effectiveFreight = isFromOrder ? (shouldForceZeroFreight ? 0 : freightValue) : manualFreight;
  const effectiveOtherExpenses = isFromOrder ? (shouldForceZeroFreight ? 0 : (orderData?.other_expenses ?? 0)) : 0;
  const invoiceTotal = Math.round((itemsTotal - effectiveDiscount + effectiveFreight + effectiveOtherExpenses) * 100) / 100;
  const installmentsTotal = invoiceInstallments.reduce((s, i) => s + i.value, 0);
  // Consistência exigida apenas na NF manual: soma das parcelas == total da nota. Na NF de
  // pedido, o grupo de pagamento deriva das parcelas do pedido (pode incluir devolução).
  const installmentsMismatch = !isFromOrder
    && shouldIncludeInstallments
    && invoiceInstallments.length > 0
    && Math.round(Math.abs(installmentsTotal - invoiceTotal) * 100) > 0;

  // Frete que o backend vai efetivamente colocar na nota. Em NF de pedido o valor da tela é
  // ignorado e re-derivado do pedido (ShippingPrice − desconto de frete), porque é ele que
  // compõe o OrderSellTotal e, logo, a soma das parcelas. Ou seja: escolher "Retirada pelo
  // Cliente" NÃO zera o frete de um pedido que cobra frete — e, com frete > 0, a modalidade
  // fiscal da nota sai 0 (por conta do remetente), não 9.
  const pickupDerivedFreight = isFromOrder && !shouldForceZeroFreight
    ? effectiveOrderFreight(orderData?.shipping_price || 0, orderData?.freight_discount_value || 0)
    : 0;

  // Endereço do destinatário (dest/enderDest): obrigatório na NF-e em qualquer modalidade,
  // inclusive na retirada pelo cliente. Checado aqui para o operador ver o que falta ANTES do
  // POST, em vez de receber o modal de erro do backend.
  const missingDestAddress = missingDestAddressFields({
    street: addrStreet,
    district: addrDistrict,
    city: addrCity,
    state: addrState,
    zip: addrZip,
  });
  // Textos do endereço acima do limite do schema (RN-NFE-73): o maxLength não corta o valor
  // que já veio do pedido, então avisamos e bloqueamos a emissão até o operador encurtar.
  const addressTooLong = destAddressTooLong([
    { label: 'Logradouro', value: addrStreet },
    { label: 'Número', value: addrNum },
    { label: 'Complemento', value: addrCompl },
    { label: 'Bairro', value: addrDistrict },
    { label: 'Cidade', value: addrCity },
  ]);
  const tooLongLength = (label: string) => addressTooLong.find(f => f.label === label)?.length;

  // ── Criar NF no Bling ───────────────────────────────────────────

  const handleCreate = async () => {
    if (invalidSalesModeForFullGift) {
      setError('Este pedido é do tipo presente integral. Emita apenas NF de bonificação.');
      return;
    }
    if (!companyId) { setError('Selecione a empresa.'); return; }
    if (!transactionTypeId) { setError('Selecione a natureza da operação.'); return; }
    if (!customerName.trim()) { setError('Informe o nome do cliente.'); return; }
    if (!customerDocument.trim()) { setError('Informe o CPF/CNPJ do cliente.'); return; }
    if (items.length === 0) { setError('Adicione pelo menos um item à nota fiscal.'); return; }
    if (itemsTotal <= 0) { setError('O valor total da nota fiscal deve ser maior que zero. Verifique se o desconto não é igual ou superior ao valor dos produtos.'); return; }
    if (!isFromOrder && invoiceTotal <= 0) { setError('O total da nota (produtos − desconto + frete) deve ser maior que zero.'); return; }
    if (installmentsMismatch) { setError('A soma das parcelas deve ser igual ao total da nota (produtos − desconto + frete).'); return; }
    if (isFromOrder && !deliveryMethod) { setError('Selecione a modalidade de entrega.'); return; }
    if (deliveryMethod === 'carrier' && !providerId) { setError('Selecione o provedor de frete.'); return; }
    if (deliveryMethod === 'carrier' && !providerServiceId) { setError('Selecione o serviço de frete.'); return; }
    if (deliveryMethod === 'carrier' && (!volumeQuantity || Number(volumeQuantity) < 1)) { setError('Informe a quantidade de volumes (mínimo 1) para envio por transportadora.'); return; }
    if (missingDestAddress.length > 0) { setError(destAddressErrorMessage(missingDestAddress)!); return; }
    if (addressTooLong.length > 0) { setError(`Encurte o endereço do destinatário (máximo ${NFE_ADDRESS_MAX_LEN} caracteres por campo na NF-e): ${addressTooLong.map(f => `${f.label} tem ${f.length}`).join(', ')}. O restante pode ir para o Complemento ou as Observações.`); return; }

    try {
      setCreating(true);
      setError(null);
      setCreateError(null);
      setValidationErrors(null);

      const installmentsPayload = shouldIncludeInstallments && invoiceInstallments.length > 0
        ? invoiceInstallments.map(({ _id, ...rest }) => rest)
        : undefined;

      // Debug: log installments being sent to help diagnose missing parcelas
      if (installmentsPayload) {
        console.debug('[InvoiceForm] Sending installments to create-bling:', JSON.stringify(installmentsPayload, null, 2));
      } else {
        console.debug('[InvoiceForm] No installments to send. shouldIncludeInstallments:', shouldIncludeInstallments, 'invoiceInstallments count:', invoiceInstallments.length);
      }

      const result = await invoiceService.createInvoiceBling({
        company_id: companyId as number,
        invoice_transaction_type_id: transactionTypeId as number,
        order_id: orderData?.order_id,
        delivery_order_id: deliveryOrderPrefill?.delivery_order_id,
        is_gift_invoice: isGiftInvoice || undefined,
        customer_id: customerId,
        tipo: tipoNF,
        finalidade,
        model,
        observacoes: observacoes || undefined,
        customer_name: customerName,
        customer_document: customerDocument,
        customer_tipo_pessoa: customerTipoPessoa,
        customer_contribuinte: customerContribuinte,
        customer_ie: customerIE || undefined,
        customer_phone: customerPhone || undefined,
        customer_email: customerEmail || undefined,
        ship_addr_street: addrStreet || undefined,
        ship_addr_num: addrNum || undefined,
        ship_addr_compl: addrCompl || undefined,
        ship_addr_district: addrDistrict || undefined,
        ship_addr_city: addrCity || undefined,
        ship_addr_state: addrState || undefined,
        ship_addr_zip: addrZip || undefined,
        shipping_service_name: shippingServiceName || undefined,
        volume_quantity: volumeQuantity !== '' ? volumeQuantity : undefined,
        total_weight: totalWeight !== '' ? totalWeight : undefined,
        is_simple_tax: isSimpleTax,
        provider_id: providerId !== '' ? providerId as number : undefined,
        provider_service_id: providerServiceId !== '' ? providerServiceId as number : undefined,
        frete_por_conta: deliveryMethod ? fretePorConta : undefined,
        frete: deliveryMethod === 'carrier'
          ? (shouldForceZeroFreight ? 0 : freightValue)
          : deliveryMethod === 'pickup'
            ? 0
            : (!isFromOrder && manualFreight > 0 ? manualFreight : undefined),
        etiqueta: deliveryMethod === 'carrier' && addrStreet ? {
          nome: customerName,
          endereco: addrStreet,
          numero: addrNum || 'S/N',
          complemento: addrCompl || undefined,
          municipio: addrCity || '',
          uf: addrState || '',
          cep: addrZip?.replace(/\D/g, '') || '',
          bairro: addrDistrict || '',
        } : undefined,
        volume: deliveryMethod === 'carrier' && volumeQuantity ? {
          quantidade: volumeQuantity as number,
          especie: 4, // 4=Caixa (sempre)
          peso_bruto: totalWeight !== '' ? totalWeight as number : 0,
          peso_liquido: totalWeight !== '' ? totalWeight as number : 0,
        } : undefined,
        transporte: deliveryMethod === 'carrier' ? {
          frete_por_conta: fretePorConta,
          frete: shouldForceZeroFreight ? 0 : freightValue,
          veiculo: veiculoPlaca ? {
            placa: veiculoPlaca,
            uf: veiculoUF,
            marca: veiculoMarca || undefined,
          } : undefined,
          transportador: transportadorNome ? {
            nome: transportadorNome,
            numero_documento: transportadorDocumento,
            ie: transportadorIE || undefined,
            endereco: transportadorEndereco ? {
              endereco: transportadorEndereco,
              municipio: transportadorMunicipio,
              uf: transportadorUF,
            } : undefined,
          } : undefined,
          volume: volumeQuantity ? {
            quantidade: volumeQuantity as number,
            especie: 4,
            peso_bruto: totalWeight !== '' ? totalWeight as number : 0,
            peso_liquido: totalWeight !== '' ? totalWeight as number : 0,
          } : undefined,
          etiqueta: addrStreet ? {
            nome: customerName,
            endereco: addrStreet,
            numero: addrNum || 'S/N',
            complemento: addrCompl || undefined,
            municipio: addrCity || '',
            uf: addrState || '',
            cep: addrZip?.replace(/\D/g, '') || '',
            bairro: addrDistrict || '',
          } : undefined,
        } as InvoiceTransportePayload : undefined,
        desconto: isFromOrder
          ? (effectiveDiscount > 0 ? effectiveDiscount : undefined)
          : (manualDiscount > 0 ? manualDiscount : undefined),
        installments: installmentsPayload,
        items: items.map(({ _tempId, ...rest }) => rest),
      });

      if (result.success) {
        // NF autorizada (ou em processamento na SEFAZ, com número reservado — o backend
        // conclui sozinho). Navegar para a listagem com flag de refresh.
        const successMessage = result.data?.pending
          ? (result.data.message || 'NF-e em processamento na SEFAZ; será concluída automaticamente.')
          : 'Nota fiscal criada com sucesso. Aguarde alguns instantes para que o status seja atualizado.';
        navigate(listPath, { state: { refresh: true, successMessage } });
      } else if (result.details && typeof result.details === 'object') {
        // HTTP 400 — campos obrigatórios ausentes ou inválidos
        setValidationErrors(result.details);
      } else if (result.error && typeof result.error === 'object') {
        // HTTP 200 com success=false — erro de negócio / Bling / SEFAZ / NCM
        // DEST_FISCAL_DIVERGENCE: o backend resolveu o Contribuinte ICMS/IE corretos
        // (consulta cadastro SEFAZ / cadastro do cliente) e os devolve em fields —
        // aplica na tela para o operador só conferir e reemitir.
        if (result.error.type === 'DEST_FISCAL_DIVERGENCE' && result.error.fields) {
          let applied = false;
          for (const field of result.error.fields) {
            if (field.element === 'customer_contribuinte' && field.msg) {
              setCustomerContribuinte(Number(field.msg));
              applied = true;
            }
            if (field.element === 'customer_ie') {
              setCustomerIE(field.msg || '');
              applied = true;
            }
          }
          if (applied) {
            result.error = {
              ...result.error,
              description: `${result.error.description || ''} Os campos Contribuinte ICMS/Inscrição Estadual da tela foram atualizados com os valores resolvidos — confira e emita novamente.`.trim(),
            };
          }
        }
        setCreateError(result.error);
      } else {
        // HTTP 500 ou resposta inesperada — usar message ou fallback
        setError(result.message || (typeof result.error === 'string' ? result.error : 'Resposta inesperada do servidor.'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar nota fiscal no Bling');
    } finally {
      setCreating(false);
    }
  };

  // ── canCreate ─────────────────────────────────────────────────
  const canCreate = Boolean(
    !invalidSalesModeForFullGift &&
    companyId &&
    transactionTypeId &&
    customerName.trim() &&
    customerDocument.trim() &&
    items.length > 0 &&
    itemsTotal > 0 &&
    (!isFromOrder || deliveryMethod) &&
    (deliveryMethod !== 'carrier' || (providerId && providerServiceId)) &&
    missingDestAddress.length === 0 &&
    addressTooLong.length === 0 &&
    !creating
  );

  // ── Pré-visualização do grupo de pagamento (NF-e) ─────────────
  // Espelha a regra do backend (BuildInvoicePaymentPlan): o desconto de troca/devolução
  // do pedido não vira desconto na nota nem a deixa negativa — ele entra como a parcela
  // "Desconto de Devolução de Produtos" (método 8), e as parcelas reais zeradas/negativas
  // (crédito) são ignoradas. É só um preview do que o backend emitirá (para pedidos, ele
  // deriva as parcelas do banco).
  const replacementDiscount = orderData?.product_replacement_discount_value ?? 0;
  const invoiceTotalPreview = itemsTotal - (isFromOrder ? (orderData?.order_discount ?? 0) : 0) + (shouldForceZeroFreight ? 0 : freightValue);
  const creditMethodIds = new Set(
    paymentMethodsLookup.filter(pm => pm.is_product_credit_4_return).map(pm => pm.payment_method_id),
  );
  const showPaymentPreview = shouldIncludeInstallments && replacementDiscount > 0;
  const paymentPreviewLines = showPaymentPreview
    ? buildInvoicePaymentPlan(
        invoiceInstallments.map(i => ({
          value: i.value,
          is_return_credit: i.payment_method_id != null && creditMethodIds.has(i.payment_method_id),
        })),
        replacementDiscount,
        invoiceTotalPreview,
      ).map(line => ({
        label: line.isReturnDiscount
          ? 'Desconto de Devolução de Produtos'
          : (invoiceInstallments[line.sourceIndex]?.payment_method_name || '—'),
        value: line.value,
        isReturnDiscount: line.isReturnDiscount,
      }))
    : [];
  const paymentPreviewTotal = paymentPreviewLines.reduce((s, l) => s + l.value, 0);

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Modals */}
      {createError && <InvoiceEmitErrorModal error={createError} contextLabel="na Emissão" onClose={() => setCreateError(null)} />}
      {validationErrors && <InvoiceValidationErrorsModal details={validationErrors} onClose={() => setValidationErrors(null)} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate(listPath)}><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isFromOrder
                ? isGiftInvoice
                  ? `Criar NF de Bonificação — Pedido #${orderData!.order_id}`
                  : `Criar Nota Fiscal — Pedido #${orderData!.order_id}`
                : 'Nova Nota Fiscal'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isFromOrder
                ? 'Confira os dados carregados do pedido e crie a nota fiscal no Bling'
                : 'Preencha os dados e crie a nota fiscal via integração Bling'}
            </p>
          </div>
        </div>
        <Button
          icon={creating ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          disabled={!canCreate}
          onClick={handleCreate}
        >
          {creating ? 'Criando...' : 'Criar Nota Fiscal'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
      )}

      {invalidSalesModeForFullGift && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Este pedido foi classificado como presente integral (tipo de pedido sem valor financeiro). A emissão permitida é apenas NF de bonificação.
        </div>
      )}

      {/* Dados Gerais */}
      <Card>
        <CardHeader title="Dados Gerais" />
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Empresa *</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={companyId}
                onChange={e => setCompanyId(e.target.value ? Number(e.target.value) : '')}
                disabled={isFromOrder || Boolean(deliveryOrderPrefill?.company_id)}
              >
                <option value="">Selecione...</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.name} — {formatCnpj(c.document)}</option>
                ))}
              </select>
              {isFromOrder && (
                <p className="text-xs text-slate-400 mt-1">Empresa do pedido (não editável)</p>
              )}
              {Boolean(deliveryOrderPrefill?.company_id) && (
                <p className="text-xs text-slate-400 mt-1">Empresa emissora do evento (não editável)</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Natureza da Operação *</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={transactionTypeId}
                onChange={e => setTransactionTypeId(e.target.value ? Number(e.target.value) : '')}
                disabled={!companyId}
              >
                <option value="">Selecione...</option>
                {transactionTypes.map(t => (
                  <option key={t.invoice_transaction_type_id} value={t.invoice_transaction_type_id}>
                    {t.invoice_transaction_type_name}
                  </option>
                ))}
              </select>
              {!companyId && <p className="text-xs text-slate-400 mt-1">Selecione a empresa primeiro</p>}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Tipo NF</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={tipoNF}
                onChange={e => setTipoNF(Number(e.target.value))}
              >
                <option value={1}>Saída</option>
                <option value={0}>Entrada</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Finalidade</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={finalidade}
                onChange={e => setFinalidade(Number(e.target.value))}
              >
                <option value={1}>Normal</option>
                <option value={2}>Complementar</option>
                <option value={3}>Ajuste</option>
                <option value={4}>Devolução</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Modelo</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={model}
                onChange={e => setModel(Number(e.target.value))}
              >
                {INVOICE_MODEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={isSimpleTax} onChange={e => setIsSimpleTax(e.target.checked)} className="accent-emerald-600 w-4 h-4" />
                Simples Nacional
              </label>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Observações</label>
              <textarea
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                rows={2}
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                placeholder="Observações adicionais da nota fiscal..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cliente */}
      <Card>
        <CardHeader title="Cliente / Destinatário" />
        <CardContent>
          {/* Customer search button */}
          {!isFromOrder && (
            <div className="mb-4">
              <Button
                variant="outline"
                icon={<Search size={16} />}
                onClick={() => setShowCustomerSearch(true)}
              >
                Buscar Cliente
              </Button>
              {customerId && (
                <span className="ml-3 text-xs text-emerald-600 font-medium">
                  Cliente vinculado (ID: {customerId})
                </span>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Nome do Cliente *</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={customerName} onChange={e => setCustomerName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">CPF/CNPJ *</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={customerDocument} onChange={e => setCustomerDocument(e.target.value)} maxLength={20} />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Tipo Pessoa *</label>
              <select className="mt-1 w-full input-field px-3 py-2 text-sm" value={customerTipoPessoa} onChange={e => setCustomerTipoPessoa(e.target.value)}>
                <option value="F">Física (CPF)</option>
                <option value="J">Jurídica (CNPJ)</option>
                <option value="E">Estrangeira</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Contribuinte ICMS *</label>
              <select className="mt-1 w-full input-field px-3 py-2 text-sm" value={customerContribuinte} onChange={e => setCustomerContribuinte(Number(e.target.value))}>
                <option value={1}>1 - Contribuinte ICMS</option>
                <option value={2}>2 - Isento</option>
                <option value={9}>9 - Não contribuinte</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Inscrição Estadual</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={customerIE} onChange={e => setCustomerIE(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Telefone</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">E-mail</label>
              <input type="email" className="mt-1 w-full input-field px-3 py-2 text-sm" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        isOpen={showCustomerSearch}
        onClose={() => setShowCustomerSearch(false)}
        onSelect={(customer: Customer) => {
          setCustomerId(customer.customer_id);
          setCustomerName(customer.name || '');
          setCustomerDocument(customer.document || '');
          const doc = (customer.document || '').replace(/\D/g, '');
          setCustomerTipoPessoa(doc.length === 14 ? 'J' : 'F');
          // UF de destino: endereço padrão do cliente (o que também preenche a tela
          // logo abaixo), com fallback para o endereço direto do cadastro ou o já digitado.
          const destUF = customer.default_address?.addr_state || customer.addr_state || addrState;
          const destFiscal = resolveDestFiscal(
            {
              document: customer.document,
              stateRegistrationTaxId: customer.state_registration_tax_id,
              nonContributorIE: customer.non_contributor_ie,
            },
            destUF
          );
          setCustomerContribuinte(destFiscal.contribuinte);
          setCustomerIE(destFiscal.ie);
          setCustomerPhone(customer.cell_phone || customer.phone || '');
          setCustomerEmail(customer.email || '');
          // Fill address from customer default address
          if (customer.default_address) {
            setAddrStreet(customer.default_address.addr_street || '');
            setAddrNum(customer.default_address.addr_num || '');
            setAddrCompl(customer.default_address.addr_compl || '');
            setAddrDistrict(customer.default_address.addr_district || '');
            setAddrCity(customer.default_address.addr_city || '');
            setAddrState(customer.default_address.addr_state || '');
            setAddrZip(customer.default_address.addr_zip || '');
          } else {
            setAddrStreet(customer.addr_street || '');
            setAddrNum(customer.addr_num || '');
            setAddrCompl(customer.addr_compl || '');
            setAddrDistrict(customer.addr_district || '');
            setAddrCity(customer.addr_city || '');
            setAddrState(customer.addr_state || '');
            setAddrZip(customer.addr_zip || '');
          }
        }}
      />

      {/* Modalidade de Entrega */}
      <Card>
        <CardHeader title="Modalidade de Entrega" />
        <CardContent>
            {loadingFreight && isFromOrder ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={16} className="animate-spin" /> Carregando cotações de frete do pedido...
              </div>
            ) : (
              <div className="space-y-4">
                {/* Delivery method selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      deliveryMethod === 'carrier'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                    onClick={() => handleDeliveryMethodChange('carrier')}
                  >
                    <Truck size={24} className={deliveryMethod === 'carrier' ? 'text-emerald-600' : 'text-slate-400'} />
                    <div className="text-left">
                      <p className="font-semibold">Envio por Transportadora</p>
                      <p className="text-xs mt-0.5">Frete CIF — produto enviado ao cliente</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      deliveryMethod === 'pickup'
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                    onClick={() => handleDeliveryMethodChange('pickup')}
                  >
                    <Package size={24} className={deliveryMethod === 'pickup' ? 'text-blue-600' : 'text-slate-400'} />
                    <div className="text-left">
                      <p className="font-semibold">Retirada pelo Cliente</p>
                      <p className="text-xs mt-0.5">Sem transporte — cliente retira na logística</p>
                    </div>
                  </button>
                </div>

                {/* Selected quote info */}
                {deliveryMethod === 'carrier' && freightQuotes.some(q => q.is_selected) && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-emerald-800 mb-1">Frete selecionado no pedido</p>
                    {freightQuotes.filter(q => q.is_selected).map(q => (
                      <div key={q.order_freight_quote_id} className="text-sm text-emerald-700">
                        <span className="font-medium">{q.provider_name}</span>
                        {q.provider_service_name && <span> — {q.provider_service_name}</span>}
                        <span className="ml-2">{formatCurrency(q.cost)}</span>
                        {q.delivery_days > 0 && <span className="text-emerald-500 ml-2">({q.delivery_days} dias úteis)</span>}
                      </div>
                    ))}
                    <p className="text-xs text-emerald-700 mt-2">
                      A cotação acima representa custo logístico interno. O valor de frete da NF-e é informado abaixo e deve refletir apenas o valor cobrado do cliente.
                    </p>
                  </div>
                )}

                {deliveryMethod === 'carrier' && (
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Valor do Frete na NF-e (R$)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="mt-1 w-full sm:w-64 input-field px-3 py-2 text-sm"
                      value={shouldForceZeroFreight ? 0 : freightValue}
                      onChange={e => setFreightValue(parseFloat(e.target.value) || 0)}
                      disabled={shouldForceZeroFreight}
                    />
                    {isFromOrder && !shouldForceZeroFreight && (
                      <p className="text-xs text-slate-500 mt-1">
                        Frete cobrado do cliente (ShippingPrice − desconto de frete do pedido). O backend recalcula esse valor na emissão a partir do pedido.
                      </p>
                    )}
                    {shouldForceZeroFreight && (
                      <p className="text-xs text-amber-700 mt-1">
                        Frete zerado automaticamente para natureza não faturada/bonificação (IsBillable = 0).
                      </p>
                    )}
                  </div>
                )}

                {/* Carrier selection when no quote is pre-selected */}
                {deliveryMethod === 'carrier' && !freightQuotes.some(q => q.is_selected) && (
                  <div className="space-y-4">
                    {loadingProviders ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 size={16} className="animate-spin" /> Carregando provedores de frete...
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Provedor de Frete *</label>
                          <select
                            className="mt-1 w-full input-field px-3 py-2 text-sm"
                            value={providerId}
                            onChange={e => setProviderId(e.target.value ? Number(e.target.value) : '')}
                          >
                            <option value="">Selecione o provedor...</option>
                            {shippingProviders.map(p => (
                              <option key={p.provider_id} value={p.provider_id}>{p.provider_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Serviço de Frete *</label>
                          <select
                            className="mt-1 w-full input-field px-3 py-2 text-sm"
                            value={providerServiceId}
                            onChange={e => setProviderServiceId(e.target.value ? Number(e.target.value) : '')}
                          >
                            <option value="">Selecione o serviço...</option>
                            {shippingServices.map(s => (
                              <option key={s.provider_service_id} value={s.provider_service_id}>{s.provider_service_name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Show available freight quotes as suggestions */}
                    {freightQuotes.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-2">Cotações disponíveis do pedido</p>
                        <div className="space-y-1.5">
                          {freightQuotes.map(q => (
                            <button
                              key={q.order_freight_quote_id}
                              type="button"
                              className={`w-full text-left p-2.5 rounded-lg border text-sm transition-all ${
                                providerId === q.provider_id && providerServiceId === q.provider_service_id
                                  ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                                  : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 text-slate-600'
                              }`}
                              onClick={() => {
                                setProviderId(q.provider_id);
                                setProviderServiceId(q.provider_service_id);
                                setShippingServiceName(q.provider_service_name || q.provider_name || '');
                              }}
                            >
                              <span className="font-medium">{q.provider_name}</span>
                              {q.provider_service_name && <span> — {q.provider_service_name}</span>}
                              <span className="ml-2 font-semibold">{formatCurrency(q.cost)}</span>
                              {q.delivery_days > 0 && <span className="text-slate-400 ml-2">({q.delivery_days} dias)</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Pickup info — reflete o frete que o backend deriva do pedido, não um zero fixo */}
                {deliveryMethod === 'pickup' && pickupDerivedFreight <= 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">Modalidade fiscal:</span> 9 — Sem Ocorrência de Transporte
                    </p>
                    <p className="text-sm text-blue-700 mt-1">Valor do frete na NF-e: R$ 0,00</p>
                  </div>
                )}
                {deliveryMethod === 'pickup' && pickupDerivedFreight > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">Modalidade fiscal:</span> 0 — Por Conta do Remetente (CIF)
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Valor do frete na NF-e: {formatCurrency(pickupDerivedFreight)}
                    </p>
                    <p className="text-xs text-amber-700 mt-2">
                      Este pedido cobra frete do cliente, e o valor do frete da nota é sempre derivado do pedido
                      (ele compõe o total e a soma das parcelas). Por isso a nota não pode sair como "sem ocorrência
                      de transporte". Se a retirada não deve cobrar frete, zere o frete no pedido antes de emitir.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

      {/* Dados de Transporte (Transportador + Veículo) */}
      {deliveryMethod === 'carrier' && (
        <Card>
          <CardHeader title={
            <span className="flex items-center gap-2">
              <Truck size={16} className="text-emerald-600" />
              Dados de Transporte
            </span>
          } />
          <CardContent className="space-y-6">
            {/* Transportador */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Transportador</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Nome</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={transportadorNome}
                    onChange={e => setTransportadorNome(e.target.value)}
                    placeholder="Nome do transportador"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">CPF/CNPJ</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={transportadorDocumento}
                    onChange={e => setTransportadorDocumento(e.target.value.replace(/\D/g, '').slice(0, 14))}
                    placeholder="00.000.000/0000-00"
                    maxLength={14}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Inscrição Estadual</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={transportadorIE}
                    onChange={e => setTransportadorIE(e.target.value)}
                    placeholder="IE do transportador"
                    maxLength={20}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Endereço Fiscal</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={transportadorEndereco}
                    onChange={e => setTransportadorEndereco(e.target.value)}
                    placeholder="Logradouro fiscal"
                    maxLength={150}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Município</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={transportadorMunicipio}
                    onChange={e => setTransportadorMunicipio(e.target.value)}
                    placeholder="Município"
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">UF</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={transportadorUF}
                    onChange={e => setTransportadorUF(e.target.value.toUpperCase())}
                    placeholder="SP"
                    maxLength={2}
                  />
                </div>
              </div>
              {providerId && transportadorNome && (
                <p className="text-xs text-emerald-600 mt-2">
                  Dados carregados automaticamente da transportadora selecionada. Edite se necessário.
                </p>
              )}
            </div>

            {/* Veículo */}
            <div className="border-t border-slate-100 pt-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Veículo de Transporte</h3>

              {/* Vehicle selection dropdown when provider has vehicles */}
              {selectedProviderHasVehicles && (
                <div className="mb-4">
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Selecionar Veículo da Frota</label>
                  {loadingVehicles ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                      <Loader2 size={14} className="animate-spin" /> Carregando veículos...
                    </div>
                  ) : (
                    <select
                      className="mt-1 w-full input-field px-3 py-2 text-sm"
                      value={selectedVehicleId}
                      onChange={e => {
                        const val = e.target.value ? Number(e.target.value) : '';
                        setSelectedVehicleId(val);
                        if (!val) {
                          setVeiculoPlaca('');
                          setVeiculoUF('');
                          setVeiculoMarca('');
                        }
                      }}
                    >
                      <option value="">Selecione um veículo ou preencha manualmente...</option>
                      {vehicles.map(v => (
                        <option key={v.vehicle_id} value={v.vehicle_id}>
                          {v.description} — {v.license_plate}
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    Transportadora com frota própria. Selecione um veículo para preencher automaticamente ou informe os dados manualmente.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Placa</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={veiculoPlaca}
                    onChange={e => setVeiculoPlaca(e.target.value.toUpperCase())}
                    placeholder="ABC-1234"
                    maxLength={20}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">UF do Veículo</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={veiculoUF}
                    onChange={e => setVeiculoUF(e.target.value.toUpperCase())}
                    placeholder="SP"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Marca</label>
                  <input
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={veiculoMarca}
                    onChange={e => setVeiculoMarca(e.target.value)}
                    placeholder="Ex: Fiat, Volkswagen"
                    maxLength={50}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Endereço do destinatário da NF-e (dest/enderDest) — obrigatório em toda nota */}
      <Card>
        <CardHeader
          title="Endereço do Destinatário (NF-e)"
          subtitle={
            deliveryMethod === 'pickup'
              ? 'Obrigatório mesmo na retirada: é o endereço do destinatário na nota, não o endereço de transporte.'
              : 'Endereço do destinatário na nota. Também alimenta a etiqueta quando há transportadora.'
          }
        />
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">CEP</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrZip} onChange={e => handleCepChange(e.target.value)} maxLength={8} placeholder="00000000" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Logradouro</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrStreet} onChange={e => setAddrStreet(e.target.value)} maxLength={NFE_ADDRESS_MAX_LEN} />
              {tooLongLength('Logradouro') !== undefined && <p className="text-xs text-red-600 mt-1">{tooLongLength('Logradouro')}/{NFE_ADDRESS_MAX_LEN} — encurte antes de emitir</p>}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Número</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrNum} onChange={e => setAddrNum(e.target.value)} maxLength={NFE_ADDRESS_MAX_LEN} />
              {tooLongLength('Número') !== undefined && <p className="text-xs text-red-600 mt-1">{tooLongLength('Número')}/{NFE_ADDRESS_MAX_LEN} — encurte antes de emitir</p>}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Complemento</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrCompl} onChange={e => setAddrCompl(e.target.value)} maxLength={NFE_ADDRESS_MAX_LEN} />
              {tooLongLength('Complemento') !== undefined && <p className="text-xs text-red-600 mt-1">{tooLongLength('Complemento')}/{NFE_ADDRESS_MAX_LEN} — encurte antes de emitir</p>}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Bairro</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrDistrict} onChange={e => setAddrDistrict(e.target.value)} maxLength={NFE_ADDRESS_MAX_LEN} />
              {tooLongLength('Bairro') !== undefined && <p className="text-xs text-red-600 mt-1">{tooLongLength('Bairro')}/{NFE_ADDRESS_MAX_LEN} — encurte antes de emitir</p>}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Cidade</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrCity} onChange={e => setAddrCity(e.target.value)} maxLength={NFE_ADDRESS_MAX_LEN} />
              {tooLongLength('Cidade') !== undefined && <p className="text-xs text-red-600 mt-1">{tooLongLength('Cidade')}/{NFE_ADDRESS_MAX_LEN} — encurte antes de emitir</p>}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">UF</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrState} onChange={e => setAddrState(e.target.value.toUpperCase())} maxLength={2} placeholder="SP" />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Transportadora</label>
              <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={shippingServiceName} onChange={e => setShippingServiceName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Qtd. Volumes</label>
              <input type="number" min="0" className="mt-1 w-full input-field px-3 py-2 text-sm" value={volumeQuantity} onChange={e => setVolumeQuantity(e.target.value ? parseInt(e.target.value) : '')} />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Peso Total (kg)</label>
              <input type="number" min="0" step="0.01" className="mt-1 w-full input-field px-3 py-2 text-sm" value={totalWeight} onChange={e => setTotalWeight(e.target.value ? parseFloat(e.target.value) : '')} />
            </div>
          </div>
          {autoCepFilled.length > 0 && missingDestAddress.length === 0 && (
            <p className="text-xs text-emerald-700 mt-3">
              Preenchido pelo CEP {addrZip}: {autoCepFilled.join(', ')}. Confira antes de emitir — o
              restante do endereço veio do pedido e não foi alterado.
            </p>
          )}
          {addressTooLong.length > 0 && (
            <p className="text-xs text-red-600 mt-3">
              A NF-e aceita no máximo {NFE_ADDRESS_MAX_LEN} caracteres por campo do endereço: {addressTooLong.map(f => `${f.label} (${f.length})`).join(', ')}.
              Encurte antes de emitir — o restante pode ir para o Complemento ou as Observações.
            </p>
          )}
          {missingDestAddress.length > 0 && (
            <p className="text-xs text-amber-600 mt-3">
              Preencha para emitir a nota: {missingDestAddress.join(', ')}. Informe o CEP para preencher
              os demais campos automaticamente.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader title="Itens da Nota Fiscal" action={
          <Button size="sm" icon={<Plus size={15} />} onClick={() => setItemShowNew(true)} disabled={itemShowNew}>
            Novo Item
          </Button>
        } />
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Código</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">NCM</th>
                  <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">Orig.</th>
                  <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">UN</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Qtd</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor Unit.</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                  <th className="py-3 px-4 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item._tempId} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-4 font-medium text-slate-900 text-sm">{item.code || '—'}</td>
                    <td className="py-2 px-4 text-slate-600 text-sm">{item.description || '—'}</td>
                    <td className="py-2 px-4 text-slate-600 text-xs">{item.ncm || '—'}</td>
                    <td className="py-2 px-4 text-center text-xs">{item.origin ?? '—'}</td>
                    <td className="py-2 px-4 text-center text-sm">{item.unit || '—'}</td>
                    <td className="py-2 px-4 text-right text-sm">{item.quantity}</td>
                    <td className="py-2 px-4 text-right text-sm">{formatCurrency(item.unit_value)}</td>
                    <td className="py-2 px-4 text-right font-medium text-sm">{formatCurrency((item.unit_value || 0) * item.quantity)}</td>
                    <td className="py-2 px-3">
                      <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded" title="Remover" onClick={() => removeItem(item._tempId)}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {/* New item form row */}
                {itemShowNew && (
                  <tr className="border-b border-emerald-100 bg-emerald-50">
                    <td className="py-2 px-4 text-sm text-slate-500">{newItemForm.code || '—'}</td>
                    <td className="py-2 px-4 relative min-w-[220px]">
                      <input
                        type="text"
                        className="w-full border border-slate-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="Buscar produto..."
                        value={itemProductQuery}
                        onChange={e => handleItemProductSearch(e.target.value)}
                        onFocus={() => { if (itemProductQuery.trim()) setItemProductOpen(true); }}
                        onBlur={() => setTimeout(() => setItemProductOpen(false), 150)}
                      />
                      {itemProductOpen && (
                        <div className="absolute left-0 top-full z-50 bg-white border border-slate-200 rounded shadow-lg w-80 max-h-48 overflow-y-auto">
                          {itemProductLoading
                            ? <div className="p-3 text-sm text-slate-400 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Buscando...</div>
                            : itemProductResults.length === 0
                              ? <div className="p-3 text-sm text-slate-400 text-center">Nenhum produto encontrado</div>
                              : itemProductResults.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-slate-100 last:border-0"
                                  onMouseDown={() => handleItemProductSelect(p)}
                                >
                                  <span className="font-medium text-slate-900">{p.sku}</span>
                                  <span className="text-slate-500 ml-2 text-xs">{p.description}</span>
                                </button>
                              ))
                          }
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-4"><input type="text" className="w-24 border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newItemForm.ncm ?? ''} onChange={e => setNewItemForm(f => ({ ...f, ncm: e.target.value }))} /></td>
                    <td className="py-2 px-4 text-center text-xs text-slate-500">{newItemForm.origin ?? 0}</td>
                    <td className="py-2 px-4 text-center"><input type="text" className="w-12 text-center border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newItemForm.unit ?? 'UN'} onChange={e => setNewItemForm(f => ({ ...f, unit: e.target.value }))} /></td>
                    <td className="py-2 px-4 text-right"><input type="number" min="0.0001" step="0.0001" className="w-20 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newItemForm.quantity ?? 1} onChange={e => setNewItemForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 1 }))} /></td>
                    <td className="py-2 px-4 text-right"><input type="number" min="0" step="0.01" className="w-24 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newItemForm.unit_value ?? 0} onChange={e => setNewItemForm(f => ({ ...f, unit_value: parseFloat(e.target.value) || 0 }))} /></td>
                    <td className="py-2 px-4 text-right text-slate-400 text-sm">—</td>
                    <td className="py-2 px-3">
                      <div className="flex gap-0.5">
                        <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Adicionar" onClick={addItem}>
                          <Plus size={13} />
                        </button>
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={() => { setItemShowNew(false); setItemProductQuery(''); }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {items.length === 0 && !itemShowNew && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-400">Nenhum item adicionado</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td colSpan={7} className="py-3 px-4 text-right font-bold text-slate-900 uppercase">Total Produtos</td>
                  <td className="py-3 px-4 text-right font-bold text-slate-900">{formatCurrency(itemsTotal)}</td>
                  <td />
                </tr>
                {isFromOrder && (orderData?.order_discount ?? 0) > 0 && (
                  <tr>
                    <td colSpan={7} className="py-1 px-4 text-right text-sm text-slate-600">Desconto do Pedido</td>
                    <td className="py-1 px-4 text-right text-red-600">-{formatCurrency(orderData?.order_discount)}</td>
                    <td />
                  </tr>
                )}
                {freightValue > 0 && (
                  <tr>
                    <td colSpan={7} className="py-1 px-4 text-right text-sm text-slate-600">Frete</td>
                    <td className="py-1 px-4 text-right text-slate-700">{formatCurrency(freightValue)}</td>
                    <td />
                  </tr>
                )}
                {effectiveOtherExpenses > 0 && (
                  <tr>
                    <td colSpan={7} className="py-1 px-4 text-right text-sm text-slate-600">Outras Despesas</td>
                    <td className="py-1 px-4 text-right text-slate-700">{formatCurrency(effectiveOtherExpenses)}</td>
                    <td />
                  </tr>
                )}
                <tr className="border-t border-slate-300">
                  <td colSpan={7} className="py-3 px-4 text-right font-bold text-slate-900 uppercase">Total Nota Fiscal</td>
                  <td className="py-3 px-4 text-right font-bold text-emerald-700 text-lg">{formatCurrency(invoiceTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Desconto e Frete — NF de venda manual (sem pedido de venda) */}
      {!isFromOrder && (
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <CreditCard size={16} className="text-emerald-600" />
                Valores da Nota
              </span>
            }
          />
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Desconto (R$)</label>
                <input
                  type="number" min="0" step="0.01"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  value={manualDiscount}
                  onChange={(e) => setManualDiscount(parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-slate-500 mt-1">Subtraído do total dos produtos.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Frete (R$)</label>
                <input
                  type="number" min="0" step="0.01"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  value={manualFreight}
                  onChange={(e) => setManualFreight(parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-slate-500 mt-1">Somado ao total dos produtos.</p>
              </div>
            </div>
            <div className="mt-4 flex justify-end text-sm">
              <span className="text-slate-600">
                Total da nota: <span className="font-bold text-emerald-700">{formatCurrency(invoiceTotal)}</span>
                <span className="text-slate-400"> (produtos {formatCurrency(itemsTotal)} − desconto {formatCurrency(manualDiscount)} + frete {formatCurrency(manualFreight)})</span>
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parcelas — exibidas para naturezas faturadas (IsBillable = 1) */}
      {shouldIncludeInstallments && (
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <CreditCard size={16} className="text-emerald-600" />
                Parcelas {isFromOrder ? 'do Pedido' : 'da Nota Fiscal'}
              </span>
            }
            action={
              <Button size="sm" variant="outline" icon={<Plus size={14} />} onClick={() => {
                const newInst: InvoiceInstallment = {
                  _id: nextInstId(),
                  value: 0,
                  due_date: new Date().toISOString().split('T')[0],
                  payment_method_id: undefined,
                  payment_method_name: '',
                  notes: '',
                };
                setInvoiceInstallments(prev => [...prev, newInst]);
                setInstallmentEditId(newInst._id);
                setInstallmentForm({ value: 0, due_date: newInst.due_date, notes: '' });
              }}>
                Adicionar Parcela
              </Button>
            }
          />
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Vencimento</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Forma de Pgto.</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Observação</th>
                    <th className="py-3 px-4 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceInstallments.map((inst, idx) => (
                    installmentEditId === inst._id ? (
                      <tr key={inst._id} className="border-b border-emerald-100 bg-emerald-50">
                        <td className="py-2 px-4 text-slate-400">{idx + 1}</td>
                        <td className="py-2 px-4">
                          <DateInputBR className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.due_date ?? ''} onChange={v => setInstallmentForm(f => ({ ...f, due_date: v }))} />
                        </td>
                        <td className="py-2 px-4 text-right">
                          <input type="number" min="0" step="0.01" className="w-28 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.value ?? 0} onChange={e => setInstallmentForm(f => ({ ...f, value: parseFloat(e.target.value) || 0 }))} />
                        </td>
                        <td className="py-2 px-4">
                          <select className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.payment_method_id ?? ''} onChange={e => { const id = parseInt(e.target.value); const pm = paymentMethodsLookup.find(p => p.payment_method_id === id); setInstallmentForm(f => ({ ...f, payment_method_id: id || undefined, payment_method_name: pm?.payment_method_name ?? '' })); }}>
                            <option value="">-- Selecione --</option>
                            {paymentMethodsLookup.map(pm => (
                              <option key={pm.payment_method_id} value={pm.payment_method_id}>{pm.payment_method_name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-4">
                          <input className="border border-slate-300 rounded px-1 py-0.5 text-sm w-36 focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.notes ?? ''} onChange={e => setInstallmentForm(f => ({ ...f, notes: e.target.value }))} placeholder="Obs." />
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex gap-0.5">
                            <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Salvar" onClick={() => {
                              setInvoiceInstallments(prev => prev.map(i => i._id === inst._id ? { ...i, ...installmentForm } as InvoiceInstallment : i));
                              setInstallmentEditId(null);
                              setInstallmentForm({});
                            }}>
                              <Save size={13} />
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={() => { setInstallmentEditId(null); setInstallmentForm({}); }}>
                              <X size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={inst._id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-4 font-medium text-slate-900">{idx + 1}</td>
                        <td className="py-2 px-4">{inst.due_date ? new Date(inst.due_date + (inst.due_date.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('pt-BR') : '—'}</td>
                        <td className="py-2 px-4 text-right font-medium">{formatCurrency(inst.value)}</td>
                        <td className="py-2 px-4 text-slate-600">{inst.payment_method_name || '—'}</td>
                        <td className="py-2 px-4 text-slate-600">{inst.notes || '—'}</td>
                        <td className="py-2 px-3">
                          <div className="flex gap-0.5">
                            <button className="p-1.5 text-slate-400 hover:text-emerald-600 transition-colors rounded" title="Editar" onClick={() => { setInstallmentEditId(inst._id); setInstallmentForm({ value: inst.value, due_date: inst.due_date, notes: inst.notes, payment_method_id: inst.payment_method_id, payment_method_name: inst.payment_method_name }); }} disabled={installmentEditId !== null}>
                              <Pencil size={13} />
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded" title="Remover" onClick={() => setInvoiceInstallments(prev => prev.filter(i => i._id !== inst._id))}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={2} className="py-3 px-4 text-right font-semibold text-slate-700">Total Parcelas</td>
                    <td className={`py-3 px-4 text-right font-bold ${installmentsMismatch ? 'text-red-600' : 'text-slate-900'}`}>{formatCurrency(installmentsTotal)}</td>
                    <td colSpan={3}></td>
                  </tr>
                  {!isFromOrder && (
                    <tr>
                      <td colSpan={2} className="py-2 px-4 text-right text-xs text-slate-500">Total da Nota (produtos − desconto + frete)</td>
                      <td className="py-2 px-4 text-right text-xs font-semibold text-slate-600">{formatCurrency(invoiceTotal)}</td>
                      <td colSpan={3}>
                        {installmentsMismatch && (
                          <span className="text-xs text-red-600 font-medium">A soma das parcelas deve ser igual ao total da nota.</span>
                        )}
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pré-visualização do grupo de pagamento da NF-e (desconto de troca/devolução) */}
      {showPaymentPreview && (
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <CreditCard size={16} className="text-purple-600" />
                Pré-visualização do pagamento na NF-e
              </span>
            }
          />
          <CardContent className="p-0">
            <div className="px-4 pt-3 pb-1">
              <p className="text-xs text-slate-500">
                O crédito de troca/devolução ({formatCurrency(replacementDiscount)}) não aparece como desconto na
                nota: ele entra como a parcela <strong>“Desconto de Devolução de Produtos”</strong>, limitada ao total
                da nota. Parcelas reais zeradas ou negativas (crédito) são ignoradas. Este é o pagamento que a nota
                fiscal emitirá.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Forma de Pgto.</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentPreviewLines.map((line, idx) => (
                    <tr key={idx} className={`border-b border-slate-100 ${line.isReturnDiscount ? 'bg-purple-50' : ''}`}>
                      <td className="py-2 px-4 font-medium text-slate-900">{idx + 1}</td>
                      <td className={`py-2 px-4 ${line.isReturnDiscount ? 'text-purple-700 font-medium' : 'text-slate-600'}`}>{line.label}</td>
                      <td className="py-2 px-4 text-right font-medium">{formatCurrency(line.value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={2} className="py-3 px-4 text-right font-semibold text-slate-700">Total do pagamento (= total da nota)</td>
                    <td className="py-3 px-4 text-right font-bold text-slate-900">{formatCurrency(paymentPreviewTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
